import { Text, View } from "@react-pdf/renderer";
import parse, { domToReact } from "html-react-parser";
import { globalStyles } from "../../pdf/DocuPdf";
import { normalizeNbsp } from "../../../helpers/stringHelpers";

interface Props {
  html: string;
  isPalletComment?: boolean;
}

// Quill serialises text color / highlight as inline styles on a <span>
// (e.g. style="color: rgb(230,0,0); background-color: rgb(255,255,0)").
// html-react-parser gives `attribs.style` as a raw string, so pull the two
// values out. `(?:^|;)\s*color` avoids matching `background-color`.
function parseInlineColor(style?: string): { color?: string; backgroundColor?: string } {
  if (!style) return {};
  const out: { color?: string; backgroundColor?: string } = {};
  const color = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
  const bg = style.match(/background-color\s*:\s*([^;]+)/i);
  if (color) out.color = color[1].trim();
  if (bg) out.backgroundColor = bg[1].trim();
  return out;
}

export const HTMLtoPDF = ({ html, isPalletComment }: Props) => {
  let listIndex = 0;

  const options: any = {
    replace: (domNode: any) => {
      if (!domNode.name) return;

      const children = domToReact(domNode.children, options);

      // Quill can attach color/highlight as an inline style on ANY inline element.
      // Read it once and merge into every text case so combined styles don't drop color.
      const inlineColor = parseInlineColor(domNode.attribs?.style);

      switch (domNode.name) {

        case "p": {
          const isEmptyP =
            !domNode.children?.length ||
            (domNode.children.length === 1 && domNode.children[0].name === "br");
          if (isEmptyP) return <Text style={{ marginBottom: 3 }} />;
          return (
            <Text style={[isPalletComment
              ? { fontSize: 8, color: "gray", lineHeight: 1.3, marginBottom: 6 }
              : { fontSize: 9, lineHeight: 1.5, marginBottom: 6 },
              inlineColor]}>
              {children}
            </Text>
          );
        }

        case "br":
          return <Text></Text>;

        case "strong":
        case "b":
          return <Text style={[globalStyles.bold, inlineColor]}>{children}</Text>;

        case "em":
        case "i":
          return <Text style={[globalStyles.italic, inlineColor]}>{children}</Text>;

        case "u":
          return <Text style={[{ textDecoration: "underline" }, inlineColor]}>{children}</Text>;

        case "span": {
          // Quill 2 injects an empty <span class="ql-ui"> as the list marker container;
          // its visual content comes from CSS ::before and must be excluded from the PDF.
          if (domNode.attribs?.class === "ql-ui") return <></>;

          if (inlineColor.color || inlineColor.backgroundColor) {
            return <Text style={inlineColor}>{children}</Text>;
          }
          return <>{children}</>;
        }

        case "h1":
          return (
            <Text style={[{ fontSize: 14, fontFamily: "Helvetica-Bold" }, inlineColor]}>
              {children}
            </Text>
          );

        case "h2":
          return (
            <Text style={[{ fontSize: 12, fontFamily: "Helvetica-Bold" }, inlineColor]}>
              {children}
            </Text>
          );

        case "h3":
          return (
            <Text style={[{ fontSize: 9, fontFamily: "Helvetica-Bold" }, inlineColor]}>
              {children}
            </Text>
          );

          case "h4":
          return (
            <Text style={[{ fontSize: 7, fontFamily: "Helvetica-Bold" }, inlineColor]}>
              {children}
            </Text>
          );

        case "ul":
          listIndex = 0;
          return <View style={{ marginBottom: 6 }}>{children}</View>;

        case "ol":
          listIndex = 0;
          return <View style={{ marginBottom: 6 }}>{children}</View>;

        case "li": {
          listIndex++;

          // Support both:
          // • Semantic HTML from getSemanticHTML(): <ul><li> (no data-list) → bullet
          // • Raw Quill 2 HTML: <ol><li data-list="ordered|bullet">
          const dataList = domNode.attribs?.["data-list"];
          const parentTag = domNode.parent?.name;
          const isOrdered =
            dataList === "ordered" ||
            (dataList === undefined && parentTag === "ol");

          return (
            <Text style={[{ marginLeft: 8, fontSize: 9 }, inlineColor]}>
              {isOrdered ? `${listIndex}. ` : "• "}
              {children}
            </Text>
          );
        }

        default:
          return;
      }
    },
  };

  return <View>{parse(normalizeNbsp(html || ""), options)}</View>;
};
